// invodiff-compare.jsx — live budget tracker panel + shared line-item status metadata.
import { TradeSectionHeader } from './invodiff-jobs.jsx';
import React from 'react';
import { firebase, fbAuth, fbDb, fbStorage } from './firebase.js';
import {
  fmtUSD, fmtDelta, safeUrl, firestoreSafe, toCSV, downloadFile, relativeTime,
  invoiceDateOf, invoiceInRange, dateRangePresets, useFocusTrap,
  ROLES, ROLE_LABEL, ROLE_COLOR,
  usesSubInvoices, useJobsStore, useJobData, invoiceStore, deleteJobDeep, useVendorRules,
  normalizeUOM, isFootageUOM, stickFeet, itemFootage, convertToQuoteBasis, summarizeMatch,
  isAutoSku, itemKeyOf, levenshtein, fracToNum, normalizeForMatch, matchNormalize, matchLineItems,
  aggregateInvoices, compareInvoices, materialState, classifyRows, MATERIAL_STATE_META,
  getJobTrades, getSegments, lineTotal, subtotal, tax, grandTotal, pricesDiffer,
  statedInvoiceTotal, statedInvoiceTax, invoiceSpend,
  SAMPLE_VENDORS, INVOICE_A, INVOICE_B, SEED_VENDOR_RULES,
} from './invodiff-data.jsx';
import { Icon } from './invodiff-input.jsx';
import { classifyVariance, DEFAULT_TOLERANCES, VARIANCE_META, VarianceBadge } from './variance-engine.jsx';
import { RowSourceLinks, DocProvenanceModal } from './doc-provenance.jsx';

// Format a quantity + optional unit of measure for display. Pure (depends only
// on its arguments), so it lives at module scope rather than being rebuilt each render.
const qtyDisplay = (qty, uom) =>
  qty != null ? `${qty.toLocaleString()}${uom ? ' ' + uom : ''}` : null;

const STATUS_META = {
  match:   { label: 'On track',        tone: 'ok',   short: '✓' },
  price:   { label: 'Price changed',   tone: 'warn', short: '$' },
  qty:     { label: 'Short shipped',   tone: 'warn', short: '#' },
  over:    { label: 'Over delivered',  tone: 'bad',  short: '↑' },
  multi:   { label: 'Multiple issues', tone: 'bad',  short: '!' },
  missing: { label: 'Not billed yet',  tone: 'warn', short: '−' },
  added:   { label: 'Not on quote',    tone: 'bad',  short: '+' },
  uom:     { label: 'Unit mismatch',   tone: 'warn', short: '≠' },
};

// ── Budget Panel (always-live right column in job view) ──────────────────
function BudgetCard({ label, value, sub, tone }) {
  return (
    <div className="bpc">
      <div className="bpc-l">{label}</div>
      <div className={`bpc-v bpc-v-${tone}`}>{value}</div>
      {sub && <div className="bpc-sub">{sub}</div>}
    </div>
  );
}

// Build a plain-English status that says THE thing about this line, with the key
// figure — e.g. "Price ▲ $0.66/u (8%)", "Short 12 LF", "Not on quote · $26.35" —
// instead of a one-word tag. Pure; module scope for a stable reference.
function budgetStatusText(row) {
  const f = row.flags || {};
  if (f.added)   return `Not on quote · billed ${fmtUSD(row.invoicedTotal || 0)}`;
  if (f.missing) return `Not billed yet · ${fmtUSD(row.quotedTotal || 0)} quoted`;
  if (f.uom)     return 'Units don’t match — review';

  const parts = [];
  if (f.price) {
    if (row.quotedUnit != null && row.invoicedAvgUnit != null) {
      const d   = row.invoicedAvgUnit - row.quotedUnit;
      const pct = row.quotedUnit ? Math.round(Math.abs(d / row.quotedUnit) * 100) : 0;
      parts.push(`Price ${d > 0 ? '▲' : '▼'} ${fmtUSD(Math.abs(d))}/u${pct ? ` (${pct}%)` : ''}`);
    } else if (row.quotedTotal != null && row.invoicedTotal != null) {
      const d = row.invoicedTotal - row.quotedTotal;
      parts.push(`${d > 0 ? '▲' : '▼'} ${fmtUSD(Math.abs(d))}`);
    }
  }
  if (f.over && row.quotedQty != null && row.invoicedQty != null) {
    parts.push(`Over by ${qtyDisplay(+(row.invoicedQty - row.quotedQty).toFixed(2), row.uomNorm)}`);
  } else if (f.qty && row.quotedQty != null && row.invoicedQty != null) {
    parts.push(`Short ${qtyDisplay(+(row.quotedQty - row.invoicedQty).toFixed(2), row.uomNorm)}`);
  }
  return parts.length ? parts.join(' · ') : 'On track';
}

function BudgetRow({ row, expanded, onToggle, quote, invoices, onPreview }) {
  const tone      = STATUS_META[row.status]?.tone || 'ok';

  // Any real row can open: matched rows show quote + invoice source snippets,
  // one-sided rows (missing / added) show whichever document they exist on.
  const canExpand = !row.synthetic && (row.quoteItem || (row.breakdown && row.breakdown.length > 0));
  const hasBreakdown = row.breakdown && row.breakdown.length > 0;
  const remaining = (row.quotedTotal != null && row.invoicedTotal != null && !row.flags?.added)
    ? +(row.quotedTotal - row.invoicedTotal).toFixed(2)
    : null;
  // Units still to be drawn down against the quote. Negative = over-ordered.
  // Items not on the quote (added) have no baseline; unreconciled-unit rows
  // (uom) would subtract apples from oranges — both are N/A.
  const qtyLeft = (row.quotedQty != null && !row.flags?.added && !row.flags?.uom)
    ? +(row.quotedQty - (row.invoicedQty || 0)).toFixed(4)
    : null;

  return (
    <>
      <div
        className={`btr ${row.resolved ? 'btr-resolved' : `btr-${tone}`} ${row.synthetic ? 'btr-synthetic' : ''} ${canExpand ? 'btr-expandable' : ''}`}
        onClick={canExpand ? onToggle : undefined}
        title={canExpand ? 'Click to see this line on the original documents' : undefined}
        {...(canExpand ? {
          role: 'button', tabIndex: 0, 'aria-expanded': !!expanded,
          onKeyDown: (e) => {
            if (e.target !== e.currentTarget) return;
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); }
          },
        } : {})}
      >
        {/* Description — SKU shown inline as a small badge */}
        <div className="btr-c btr-c-desc">
          <span className="btr-desc-txt" title={row.desc}>{row.desc}</span>
          {!row.synthetic && row.sku && row.sku !== '—' && (
            <span className="btr-sku-inline mono">{row.sku}</span>
          )}
          {row.synthetic && <span className="btr-badge btr-syn">{row.synthetic}</span>}
          {canExpand && <span className="btr-chevron">{expanded ? '▴' : '▾'}</span>}
        </div>
        {/* Qtd qty */}
        <div className={`btr-c btr-c-qty num ${row.flags?.missing ? 'btr-dim' : ''}`}>
          {qtyDisplay(row.quotedQty, row.quotedUOM || row.uomNorm)
            ?? <span className="btr-dash">—</span>}
        </div>
        {/* Inv qty — labeled with the invoice's own unit when unreconciled */}
        <div className={`btr-c btr-c-qty num ${(row.flags?.qty || row.flags?.over || row.flags?.added || row.flags?.uom) ? 'btr-flag' : ''}`}>
          {qtyDisplay(row.invoicedQty, row.invoicedUOM ?? row.uomNorm)
            ?? <span className="btr-dash">—</span>}
        </div>
        {/* Qty left — units remaining on the quote (negative = over-ordered) */}
        <div className="btr-c btr-c-qty num">
          {qtyLeft != null
            ? (qtyLeft < 0
                ? <span className="btr-over">−{qtyDisplay(Math.abs(qtyLeft), row.quotedUOM || row.uomNorm)}</span>
                : <span className={qtyLeft === 0 ? '' : 'btr-ok'}>{qtyDisplay(qtyLeft, row.quotedUOM || row.uomNorm)}</span>)
            : <span className="btr-dash">—</span>}
        </div>
        {/* Quoted unit price — the baseline (muted when the invoice differs). */}
        <div className={`btr-c btr-c-num num ${row.flags?.price && !row.flags?.missing ? 'btr-unit-base' : ''}`}>
          {row.quotedUnit != null ? fmtUSD(row.quotedUnit) : <span className="btr-dash">—</span>}
        </div>
        {/* Invoiced avg unit price — show the signed difference vs. quoted so the
            change is legible (instead of striking out the quoted price). */}
        <div className={`btr-c btr-c-num num ${row.flags?.price || row.flags?.added ? 'btr-flag' : ''}`}>
          {row.invoicedAvgUnit != null ? (
            <span className="btr-unit-wrap">
              <span>{fmtUSD(row.invoicedAvgUnit)}</span>
              {row.flags?.price && !row.flags?.missing && row.quotedUnit != null && (
                <span className={`btr-unit-delta ${row.invoicedAvgUnit > row.quotedUnit ? 'btr-up' : 'btr-down'}`}>
                  {row.invoicedAvgUnit > row.quotedUnit ? '▲' : '▼'} {fmtUSD(Math.abs(row.invoicedAvgUnit - row.quotedUnit))}
                </span>
              )}
            </span>
          ) : <span className="btr-dash">—</span>}
        </div>
        {/* Quoted ext $ */}
        <div className="btr-c btr-c-num num">
          {row.quotedTotal != null ? fmtUSD(row.quotedTotal) : <span className="btr-dash">—</span>}
        </div>
        {/* Invoiced ext $ */}
        <div className={`btr-c btr-c-num num ${row.flags?.price || row.flags?.added ? 'btr-flag' : ''}`}>
          {row.invoicedTotal != null && row.invoicedTotal !== 0
            ? fmtUSD(row.invoicedTotal) : <span className="btr-dash">—</span>}
        </div>
        {/* Remaining */}
        <div className="btr-c btr-c-num num">
          {row.flags?.added
            ? <span className="btr-over">−{fmtUSD(row.invoicedTotal)}</span>
            : remaining != null
              ? (remaining < 0
                  ? <span className="btr-over">−{fmtUSD(Math.abs(remaining))}</span>
                  : <span className={remaining === 0 ? '' : 'btr-ok'}>{fmtUSD(remaining)}</span>)
              : <span className="btr-dash">—</span>
          }
        </div>
        {/* Status — a plain-English summary of the line's main point + key figure */}
        <div className="btr-c btr-c-status">
          <span className={`btr-st btr-st-${row.resolved ? 'info' : tone}`}>
            <span className="btr-st-dot" />
            <span className="btr-st-txt">{row.resolved ? 'Resolved' : budgetStatusText(row)}</span>
          </span>
        </div>
      </div>

      {/* Per-invoice breakdown — shown when row is expanded */}
      {expanded && canExpand && (
        <div className="btr-breakdown">
          {/* Quote baseline reference */}
          {row.quotedUnit != null && (
            <div className="btr-bd-quote">
              <span className="btr-bd-q-label">Quote</span>
              <span className="num">{row.quotedQty?.toLocaleString()}{row.uomNorm ? ' ' + row.uomNorm : ''}</span>
              <span className="btr-bd-q-at">@</span>
              <span className="num btr-bd-q-unit">{fmtUSD(row.quotedUnit)}</span>
              <span className="btr-bd-q-at">=</span>
              <span className="num">{fmtUSD(row.quotedTotal)}</span>
            </div>
          )}
          {hasBreakdown && <div className="btr-bd-head">
            <span>Invoice</span>
            <span>Date</span>
            <span className="num">Qty</span>
            <span className="num">Unit price</span>
            <span className="num">Line total</span>
          </div>}
          {row.breakdown.map((b) => (
            <div key={b.invoiceId} className="btr-bd-row">
              <span className="mono btr-bd-id">{b.invoiceId}</span>
              <span className="btr-bd-date">{b.date}</span>
              <span className="num">
                {b.qty.toLocaleString()}{row.uomNorm ? ' ' + row.uomNorm : ''}
                {/* billed on a different basis — show what the invoice printed */}
                {b.srcQty != null && <span className="btr-bd-src"> ({b.srcQty.toLocaleString()} {b.srcUom})</span>}
              </span>
              <span className={`num ${row.quotedUnit != null && b.unit !== row.quotedUnit ? 'btr-bd-unit-diff' : ''}`}>
                {fmtUSD(b.unit)}{row.uomNorm === 'LF' ? '/LF' : ''}
              </span>
              <span className="num">{fmtUSD(b.lineTotal)}</span>
            </div>
          ))}
          <RowSourceLinks row={row} quote={quote} invoices={invoices} onPreview={onPreview} />
        </div>
      )}
    </>
  );
}

// Spend-composition visualization for the Summary — answers "are we on budget,
// where did the money go, and what's driving cost" at a glance, before the
// full line-item table.
function BudgetViz({ summary, remaining, pctRaw, overBudget, addedTotal, quotedRows, addedRows }) {
  const onQuote  = +(summary.invoicedTotal - addedTotal).toFixed(2);
  const usedPct  = Math.round(pctRaw * 100);
  const fillPct  = Math.min(pctRaw, 1) * 100;
  const overAmt  = overBudget ? Math.abs(remaining) : 0;
  const spend    = summary.invoicedTotal || 1;
  const onQuotePct = Math.max(0, (onQuote / spend) * 100);
  const newPct     = Math.max(0, (addedTotal / spend) * 100);

  const drivers = quotedRows.concat(addedRows)
    .filter((r) => r.invoicedTotal > 0)
    .sort((a, b) => b.invoicedTotal - a.invoicedTotal)
    .slice(0, 5);
  const maxDriver = drivers.length ? drivers[0].invoicedTotal : 1;

  return (
    <div className="bv">
      <div className="bv-block">
        <div className="bv-row">
          <span className="bv-lbl">Budget used</span>
          <span className={`bv-amt num ${overBudget ? 'bv-bad' : ''}`}>
            {fmtUSD(summary.invoicedTotal)} <span className="bv-of">of {fmtUSD(summary.quotedTotal)}</span> · {usedPct}%
          </span>
        </div>
        <div className="bv-track">
          <span className={`bv-fill ${overBudget ? 'bv-fill-bad' : pctRaw > 0.8 ? 'bv-fill-warn' : 'bv-fill-ok'}`}
            style={{ width: `${fillPct}%` }} />
        </div>
        {overAmt > 0 && <div className="bv-overlbl num">{fmtUSD(overAmt)} over budget</div>}
      </div>

      {summary.invoicedTotal > 0 && addedTotal > 0 && (
        <div className="bv-block">
          <div className="bv-row"><span className="bv-lbl">Where the spend went</span></div>
          <div className="bv-split">
            <span className="bv-seg-fill bv-seg-ok"  style={{ width: `${onQuotePct}%` }} title={`On quote: ${fmtUSD(onQuote)}`} />
            <span className="bv-seg-fill bv-seg-bad" style={{ width: `${newPct}%` }} title={`New material: ${fmtUSD(addedTotal)}`} />
          </div>
          <div className="bv-legend">
            <span className="bv-leg"><span className="bv-dot bv-dot-ok" /> On quote <span className="num bv-leg-amt">{fmtUSD(onQuote)}</span></span>
            <span className="bv-leg"><span className="bv-dot bv-dot-bad" /> New material <span className="num bv-leg-amt">{fmtUSD(addedTotal)}</span></span>
          </div>
        </div>
      )}

      {drivers.length > 0 && (
        <div className="bv-block">
          <div className="bv-row"><span className="bv-lbl">Top cost drivers</span></div>
          <div className="bv-drivers">
            {drivers.map((r) => (
              <div key={`${r.sku}|${r.desc}|${r.invoicedTotal}`} className="bv-driver">
                <span className="bv-driver-desc" title={r.desc}>
                  {r.flags?.added && <span className="bv-driver-tag">NEW</span>}{r.desc}
                </span>
                <span className="bv-driver-bar">
                  <span className={`bv-driver-fill ${r.flags?.added ? 'bv-seg-bad' : 'bv-seg-ok'}`}
                    style={{ width: `${(r.invoicedTotal / maxDriver) * 100}%` }} />
                </span>
                <span className="bv-driver-amt num">{fmtUSD(r.invoicedTotal)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BudgetPanel({ quote, invoices, vendorRules, taxExempt }) {
  const vendor  = quote?.vendor || '';
  // Memoized so a vendor with no rules yields ONE empty array, not a new one
  // per render — `rules` keys the comparison below, and a fresh identity
  // re-ran the whole matcher on every render (each row expand included).
  const rules   = React.useMemo(() => (vendorRules || {})[vendor] || [], [vendorRules, vendor]);
  // Expansion is keyed per row (not by SKU — added/no-SKU rows share '—').
  const [expandedRow, setExpandedRow] = React.useState(null);
  const [preview, setPreview] = React.useState(null); // { doc, sel } for the popup PDF viewer

  const comparison = React.useMemo(() => {
    if (!quote || !invoices || invoices.length === 0) return null;
    return aggregateInvoices(quote, invoices, rules, { taxExempt });
  }, [quote, invoices, rules, taxExempt]);

  const toggleRow = (id) => setExpandedRow((prev) => (prev === id ? null : id));

  if (!quote) {
    return (
      <div className="bp bp-empty">
        <div className="bp-icon"><Icon name="delta" size={26} /></div>
        <div className="bp-empty-h">Budget tracker</div>
        <div className="bp-empty-sub">Upload a quote to start tracking budget.</div>
      </div>
    );
  }

  if (!comparison) {
    return (
      <div className="bp bp-empty">
        <div className="bp-icon"><Icon name="delta" size={26} /></div>
        <div className="bp-empty-h">Budget tracker</div>
        <div className="bp-wait-val num">{fmtUSD(taxExempt ? +(subtotal(quote) + (quote.shipping || 0)).toFixed(2) : grandTotal(quote))}</div>
        <div className="bp-wait-lbl">Total quoted{taxExempt ? ' (pre-tax)' : ''}</div>
        <div className="bp-empty-sub">Add invoices to track spending.</div>
      </div>
    );
  }

  const { summary, rows } = comparison;
  const quotedRows    = rows.filter((r) => !r.flags?.added && !r.synthetic);
  const addedRows     = rows.filter((r) => r.flags?.added);
  const syntheticRows = rows.filter((r) => r.synthetic);

  const remaining  = +(summary.quotedTotal - summary.invoicedTotal).toFixed(2);
  const pctRaw     = summary.quotedTotal > 0 ? summary.invoicedTotal / summary.quotedTotal : 0;
  const overBudget = remaining < 0;
  const addedTotal = +addedRows.reduce((s, r) => s + r.invoicedTotal, 0).toFixed(2);

  // Footage: rows billed in LF directly, plus footage derived from stick
  // count × stick length (e.g. "27 LN of 1-1/2x20 FT tube" → 540 LF). Include
  // off-quote "added" rows in the INVOICED running total — a size swap or
  // substitution billed off-quote still draws down real footage; they carry no
  // quotedLF, so they add to invoiced only, not the quoted baseline.
  const lfRows        = rows.filter((r) => !r.synthetic
    && (r.quotedLF != null || r.invoicedLF != null || r.uomNorm === 'LF'));
  const lfQuoted      = lfRows.reduce((s, r) => s + (r.quotedLF   ?? (r.uomNorm === 'LF' ? r.quotedQty   || 0 : 0)), 0);
  const lfInvoiced    = lfRows.reduce((s, r) => s + (r.invoicedLF ?? (r.uomNorm === 'LF' ? r.invoicedQty || 0 : 0)), 0);
  const hasFootage    = lfRows.length > 0;

  return (
    <div className="bp">
      {/* Summary cards */}
      <div className={`bp-cards bp-cards-${hasFootage ? '5' : '4'}`}>
        <BudgetCard label="Quoted"    value={fmtUSD(summary.quotedTotal)}   tone="info" />
        <BudgetCard label="Invoiced"  value={fmtUSD(summary.invoicedTotal)} tone={overBudget ? 'bad' : 'warn'} />
        <BudgetCard
          label="Remaining"
          value={<>{overBudget && <span className="bp-sign">−</span>}{fmtUSD(Math.abs(remaining))}</>}
          tone={overBudget ? 'bad' : 'ok'}
        />
        <BudgetCard
          label="Used"
          value={Math.round(pctRaw * 100) + '%'}
          tone={pctRaw > 1 ? 'bad' : pctRaw > 0.8 ? 'warn' : 'ok'}
        />
        {hasFootage && (
          <BudgetCard
            label="Footage"
            value={<>{lfInvoiced.toLocaleString()} LF<span className="bpc-v-cmp"> / {lfQuoted.toLocaleString()} LF quoted</span></>}
            sub="invoiced / quoted"
            tone={lfInvoiced > lfQuoted ? 'warn' : 'info'}
          />
        )}
      </div>

      {/* Spend composition — the at-a-glance answer */}
      <BudgetViz
        summary={summary} remaining={remaining} pctRaw={pctRaw} overBudget={overBudget}
        addedTotal={addedTotal} quotedRows={quotedRows} addedRows={addedRows}
      />

      {/* Full line-item breakdown — progressive disclosure (collapsed by default) */}
      <details className="bp-disc">
        <summary className="bp-disc-sum">
          <Icon name="rule" size={14} />
          <span>Full budget breakdown</span>
          <span className="bp-disc-c num">{quotedRows.length} material{quotedRows.length !== 1 ? 's' : ''}{addedRows.length > 0 ? ` · ${addedRows.length} new` : ''}</span>
          <Icon name="chev" size={13} className="bp-disc-chev" />
        </summary>
        <div className="bp-disc-body">
      {/* Main budget table */}
      <div className="bt">
        <div className="bt-head">
          <div className="bt-h bt-h-desc">Description</div>
          <div className="bt-h bt-h-qty">Qtd qty</div>
          <div className="bt-h bt-h-qty">Inv qty</div>
          <div className="bt-h bt-h-qty">Qty left</div>
          <div className="bt-h bt-h-num">Qtd unit</div>
          <div className="bt-h bt-h-num">Inv unit</div>
          <div className="bt-h bt-h-num">Quoted $</div>
          <div className="bt-h bt-h-num">Invoiced $</div>
          <div className="bt-h bt-h-num">Remaining</div>
          <div className="bt-h bt-h-st">Status</div>
        </div>
        <div className="bt-body">
          {quotedRows.map((r, i) => (
            <BudgetRow key={r.sku + i} row={r} quote={quote} invoices={invoices}
              expanded={expandedRow === 'q' + i}
              onToggle={() => toggleRow('q' + i)}
              onPreview={(doc, sel) => setPreview({ doc, sel })} />
          ))}
          {syntheticRows.map((r, i) => (
            <BudgetRow key={(r.synthetic || r.sku) + i} row={r}
              expanded={false} onToggle={() => {}} />
          ))}
        </div>
        <div className="bt-foot">
          <div className="bt-f bt-f-lbl">Total</div>
          <div className="bt-f bt-f-num num">{fmtUSD(summary.quotedTotal)}</div>
          <div className="bt-f bt-f-num num">{fmtUSD(summary.invoicedTotal)}</div>
          <div className={`bt-f bt-f-num num ${overBudget ? 'btr-over' : 'btr-ok'}`}>
            {overBudget ? '−' : ''}{fmtUSD(Math.abs(remaining))}
          </div>
          <div className="bt-f"></div>
        </div>
      </div>

      {/* New Material section (off-quote — no RFQ line matched above 60%) */}
      {addedRows.length > 0 && (
        <div className="bt bt-added">
          <div className="bt-added-hd">
            <Icon name="flag" size={13} />
            New Material — Not on Quote
            <span className="bt-added-amt num">{fmtUSD(addedTotal)}</span>
          </div>
          <div className="bt-body">
            {addedRows.map((r, i) => (
              <BudgetRow key={r.sku + i} row={r} quote={quote} invoices={invoices}
                expanded={expandedRow === 'a' + i}
                onToggle={() => toggleRow('a' + i)}
                onPreview={(doc, sel) => setPreview({ doc, sel })} />
            ))}
          </div>
        </div>
      )}
        </div>
      </details>

      {preview && (
        <DocProvenanceModal invoice={preview.doc} initialSel={preview.sel}
          onClose={() => setPreview(null)} />
      )}

      <BudgetPanelStyles />
    </div>
  );
}

const BudgetPanelStyles = () => (
  <style>{`
    .bp {
      background: var(--surface); border: 1px solid var(--border);
      border-radius: var(--r-lg); overflow: hidden; box-shadow: var(--shadow-1);
    }
    .bp-empty {
      padding: 48px 24px; display: flex; flex-direction: column;
      align-items: center; text-align: center; gap: 10px;
    }
    .bp-icon { color: var(--fg-dim); opacity: 0.3; }

    /* Spend-composition viz */
    .bv { display: flex; flex-direction: column; gap: 16px; padding: 16px 18px; border-top: 1px solid var(--border); }
    .bv-block { display: flex; flex-direction: column; gap: 7px; }
    .bv-row { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
    .bv-lbl { font-size: 10.5px; text-transform: uppercase; letter-spacing: .05em; color: var(--fg-dim); }
    .bv-amt { font-size: 13px; color: var(--fg-muted); font-variant-numeric: tabular-nums; }
    .bv-amt.bv-bad { color: var(--bad); }
    .bv-of { color: var(--fg-dim); }
    .bv-track { height: 12px; border-radius: 6px; background: var(--surface-3); overflow: hidden; }
    .bv-fill { display: block; height: 100%; border-radius: 6px; transition: width .3s ease; }
    .bv-fill-ok   { background: var(--ok); }
    .bv-fill-warn { background: var(--warn); }
    .bv-fill-bad  { background: var(--bad); }
    .bv-overlbl { font-size: 11.5px; color: var(--bad); font-weight: 600; }
    .bv-split { display: flex; height: 12px; border-radius: 6px; overflow: hidden; background: var(--surface-3); gap: 2px; }
    .bv-seg-fill { height: 100%; border-radius: 2px; }
    .bv-seg-ok  { background: var(--ok); }
    .bv-seg-bad { background: var(--bad); }
    .bv-legend { display: flex; flex-wrap: wrap; gap: 6px 18px; }
    .bv-leg { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: var(--fg-muted); }
    .bv-dot { width: 9px; height: 9px; border-radius: 2px; }
    .bv-dot-ok  { background: var(--ok); }
    .bv-dot-bad { background: var(--bad); }
    .bv-leg-amt { color: var(--fg); font-weight: 600; font-variant-numeric: tabular-nums; }
    .bv-drivers { display: flex; flex-direction: column; gap: 6px; }
    .bv-driver { display: grid; grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr) auto; gap: 10px; align-items: center; }
    .bv-driver-desc { font-size: 12.5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .bv-driver-tag { font-size: 8.5px; font-weight: 700; color: var(--bad); background: var(--bad-bg); border-radius: 3px; padding: 1px 4px; margin-right: 6px; vertical-align: middle; }
    .bv-driver-bar { height: 8px; background: var(--surface-3); border-radius: 4px; overflow: hidden; }
    .bv-driver-fill { display: block; height: 100%; border-radius: 4px; }
    .bv-driver-amt { font-size: 12.5px; font-weight: 600; font-variant-numeric: tabular-nums; min-width: 76px; text-align: right; }

    /* Collapsible full breakdown */
    .bp-disc { border-top: 1px solid var(--border); }
    .bp-disc-sum { display: flex; align-items: center; gap: 8px; padding: 12px 18px; cursor: pointer; font-size: 13px; font-weight: 500; color: var(--fg-muted); list-style: none; user-select: none; }
    .bp-disc-sum::-webkit-details-marker { display: none; }
    .bp-disc-sum:hover { background: var(--surface-2); }
    .bp-disc-c { margin-left: auto; font-size: 11.5px; color: var(--fg-dim); }
    .bp-disc-chev { transition: transform .2s; color: var(--fg-dim); }
    .bp-disc[open] .bp-disc-chev { transform: rotate(90deg); }
    .bp-empty-h { font-size: 14px; font-weight: 600; color: var(--fg-muted); }
    .bp-empty-sub { font-size: 12.5px; color: var(--fg-dim); line-height: 1.55; max-width: 220px; }
    .bp-wait-val { font-size: 30px; font-weight: 600; color: var(--info); letter-spacing: -0.025em; font-family: "IBM Plex Mono", monospace; }
    .bp-wait-lbl { font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--fg-dim); margin-top: -4px; }
    .bp-sign { opacity: 0.7; }

    .bp-cards { display: grid; border-bottom: 1px solid var(--border); }
    .bp-cards-4 { grid-template-columns: repeat(4, 1fr); }
    .bp-cards-5 { grid-template-columns: repeat(5, 1fr); }
    .bpc { padding: 11px 12px; border-right: 1px solid var(--border); display: flex; flex-direction: column; gap: 2px; min-width: 0; }
    .bpc:last-child { border-right: none; }
    .bpc-l { font-size: 9.5px; color: var(--fg-dim); letter-spacing: 0.07em; text-transform: uppercase; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .bpc-v { font-size: 15px; font-weight: 600; font-family: "IBM Plex Mono", monospace; letter-spacing: -0.02em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: flex; align-items: baseline; gap: 1px; }
    .bpc-sub { font-size: 10px; color: var(--fg-dim); line-height: 1.4; margin-top: 1px; }
    .bpc-v-ok   { color: var(--ok);   }
    .bpc-v-warn { color: var(--warn); }
    .bpc-v-bad  { color: var(--bad);  }
    .bpc-v-info { color: var(--info); }
    /* dim "quoted" comparator on the footage card so the invoiced LF reads first */
    .bpc-v-cmp { font-size: 11px; font-weight: 500; color: var(--fg-dim); letter-spacing: 0; }

    /* Budget table */
    .bt { display: flex; flex-direction: column; overflow-x: auto; }
    .bt-head, .btr, .bt-foot {
      display: grid;
      grid-template-columns: minmax(140px,1fr) 64px 64px 70px 84px 84px 82px 82px 96px 216px;
    }
    /* Dense 10-col table: scroll horizontally instead of clipping under .bp's
       overflow:hidden — the job's right column is < 914px on laptop/tablet widths. */
    .bt-head, .bt-body, .bt-foot, .bt-added-hd { min-width: 988px; }
    .bt-head { background: var(--surface-2); border-bottom: 1px solid var(--border); }
    .bt-h {
      font-size: 9px; letter-spacing: 0.08em; text-transform: uppercase;
      color: var(--fg-dim); font-weight: 500; padding: 6px 8px;
      border-right: 1px solid var(--border);
    }
    .bt-h:last-child { border-right: none; }
    .bt-h-num, .bt-h-qty { text-align: right; }
    .bt-h-st  { text-align: left; }

    .btr { border-bottom: 1px solid var(--border); min-height: 34px; align-items: stretch; position: relative; }
    .btr:last-child { border-bottom: none; }
    .btr-expandable { cursor: pointer; }
    .btr-expandable:hover { background: var(--surface-2); }
    .btr-warn::before, .btr-bad::before {
      content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 2px;
    }
    .btr-warn::before { background: var(--warn); }
    .btr-bad::before  { background: var(--bad);  }
    .btr-resolved  { background: oklch(from var(--info) l c h / 0.05); }
    .btr-synthetic { background: var(--surface-2); }

    .btr-c {
      padding: 0 8px; font-size: 11px; color: var(--fg);
      border-right: 1px solid var(--border); min-height: 34px; display: flex; align-items: center;
    }
    .btr-c:last-child { border-right: none; }
    .btr-c-num  { justify-content: flex-end; }
    .btr-c-qty  { justify-content: flex-end; font-size: 10.5px; }
    .btr-c-desc { gap: 5px; overflow: hidden; }
    .btr-c-status { justify-content: flex-start; overflow: visible; }
    /* Descriptive status: a tone dot + a short plain-English summary that may wrap
       to two lines (the column is widened to fit it). */
    .btr-st { display: flex; align-items: flex-start; gap: 6px; font-size: 10.5px; line-height: 1.28; }
    .btr-st-dot { width: 7px; height: 7px; border-radius: 50%; margin-top: 2.5px; flex-shrink: 0; background: var(--fg-dim); }
    .btr-st-txt { min-width: 0; }
    .btr-st-ok   { color: var(--ok);   } .btr-st-ok   .btr-st-dot { background: var(--ok);   }
    .btr-st-warn { color: var(--warn); } .btr-st-warn .btr-st-dot { background: var(--warn); }
    .btr-st-bad  { color: var(--bad);  } .btr-st-bad  .btr-st-dot { background: var(--bad);  }
    .btr-st-info { color: var(--info); } .btr-st-info .btr-st-dot { background: var(--info); }
    .btr-desc-txt { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; font-size: 11.5px; flex: 1; }
    .btr-sku-inline { font-size: 9.5px; color: var(--fg-dim); background: var(--surface-3); border: 1px solid var(--border); padding: 1px 5px; border-radius: 3px; flex-shrink: 0; white-space: nowrap; }
    /* Quoted unit as the baseline when the invoice differs: muted, NOT struck
       through (the difference is shown by the delta on the invoiced unit). */
    .btr-unit-base { color: var(--fg-muted); }
    .btr-unit-wrap { display: inline-flex; flex-direction: column; align-items: flex-end; line-height: 1.15; }
    .btr-unit-delta { font-size: 9px; font-weight: 600; white-space: nowrap; }
    .btr-unit-delta.btr-up   { color: var(--bad); }
    .btr-unit-delta.btr-down { color: var(--ok); }
    .btr-badge { font-size: 9px; font-family: "IBM Plex Mono", monospace; color: var(--fg-dim); background: var(--surface-3); border: 1px solid var(--border); padding: 1px 4px; border-radius: 3px; flex-shrink: 0; letter-spacing: 0.03em; }
    .btr-syn   { text-transform: uppercase; letter-spacing: 0.06em; }
    .btr-dash  { color: var(--fg-dim); }
    .btr-over  { color: var(--bad); font-weight: 500; }
    .btr-ok    { color: var(--ok); }
    /* Pending (not-yet-invoiced) quote qty — dim it, but never strike it
       through: the quantity is still outstanding, not cancelled. */
    .btr-dim   { color: var(--fg-dim); }
    .btr-flag  { color: var(--warn); font-weight: 500; }
    .btr-chevron { font-size: 8px; color: var(--fg-dim); flex-shrink: 0; }

    /* Per-invoice breakdown */
    .btr-breakdown {
      border-bottom: 1px solid var(--border); background: var(--surface-2);
      padding: 8px 10px 10px;
    }
    .btr-bd-quote {
      display: flex; align-items: center; gap: 6px;
      padding: 3px 4px 7px; margin-bottom: 5px;
      border-bottom: 1px dashed var(--border);
      font-size: 11px; color: var(--fg-muted);
    }
    .btr-bd-q-label {
      font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em;
      color: var(--fg-dim); background: var(--surface-3); padding: 1px 5px; border-radius: 3px;
      border: 1px solid var(--border); flex-shrink: 0;
    }
    .btr-bd-q-at { color: var(--fg-dim); }
    .btr-bd-q-unit { color: var(--info); font-weight: 600; }
    .btr-bd-unit-diff { color: var(--warn); font-weight: 600; }
    .btr-bd-head, .btr-bd-row {
      display: grid; grid-template-columns: 90px 78px 1fr 88px 80px;
      gap: 8px; font-size: 11px; padding: 3px 4px;
    }
    .btr-bd-head { color: var(--fg-dim); font-weight: 500; font-size: 10px; letter-spacing: 0.04em; text-transform: uppercase; border-bottom: 1px solid var(--border); padding-bottom: 5px; margin-bottom: 3px; }
    .btr-bd-row { color: var(--fg-muted); border-radius: var(--r-sm); }
    .btr-bd-row:hover { background: var(--surface-3); }
    .btr-bd-id   { color: var(--accent); font-size: 10.5px; overflow: hidden; text-overflow: ellipsis; }
    .btr-bd-date { color: var(--fg-dim); }
    .btr-bd-src  { color: var(--fg-dim); font-size: 10px; }

    .bt-foot { background: var(--surface-2); border-top: 1px solid var(--border-strong); min-height: 34px; align-items: center; }
    .bt-f { padding: 0 8px; font-size: 11px; }
    .bt-f-lbl { grid-column: 1 / 7; font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.07em; color: var(--fg-dim); font-weight: 500; }
    .bt-f-num { text-align: right; font-weight: 600; }

    .bt-added { border-top: 2px solid oklch(from var(--bad) l c h / 0.30); }
    .bt-added-hd {
      display: flex; align-items: center; gap: 8px;
      padding: 8px 12px; background: oklch(from var(--bad) l c h / 0.07);
      border-bottom: 1px solid oklch(from var(--bad) l c h / 0.18);
      font-size: 12px; font-weight: 600; color: var(--bad);
    }
    .bt-added-hd > :nth-child(3) { flex: 1; } /* pushes total to right */
    .bt-added-amt { font-size: 12.5px; }
  `}</style>
);

// ── SummaryView (job "answer" landing — the budget at a glance) ──────────
// Read-only roll-up of where the job stands: the live budget tracker plus an
// issues callout and quick links into the working tabs. The matching/diagnostic
// machinery stays out of sight here — this tab answers "are we on budget?".
function SummaryView({ job, userId, vendorRules, onNavTab, selectedTrades }) {
  const [quote, setQuote]       = React.useState(null);
  const [quotesByTrade, setQuotesByTrade] = React.useState({});
  const [trades, setTrades]     = React.useState(() => getJobTrades(job));
  const [invoices, setInvoices] = React.useState([]);
  const [tolerances, setTols]   = React.useState(null);
  const [taxExempt, setTaxExempt] = React.useState(!!job.taxExempt);
  const [synced, setSynced]     = React.useState(false);

  const isSample = job.id === '_sample';
  const jobRef   = React.useMemo(
    () => !isSample ? fbDb.collection('jobs').doc(job.id) : null,
    [job.id]
  );

  // Live job doc + invoices via the shared dual-shape hook (invodiff-data.jsx)
  // — resolves the invoices array from whichever storage shape the job uses.
  const live = useJobData(isSample ? null : job.id);
  React.useEffect(() => {
    if (isSample) {
      setQuote(INVOICE_A);
      setInvoices([INVOICE_B]);
      setSynced(true);
      return;
    }
    if (!live.synced) { setSynced(false); return; }
    if (live.exists) {
      const d = live.data;
      setQuote(d.quote ?? null);
      setQuotesByTrade(d.quotesByTrade ?? {});
      setTrades(getJobTrades(d));
      setInvoices(live.invoices);
      setTols(d.tolerances || null);
      setTaxExempt(!!d.taxExempt);
    }
    setSynced(true);
  }, [isSample, live]);

  const visible = React.useMemo(() => invoices.filter((inv) => !inv._hidden), [invoices]);
  const multiTrade = trades.length > 1;

  // One comparison per trade segment (single-trade jobs collapse to one segment
  // over the legacy quote, so totals are unchanged). Each segment carries its
  // own vendor rules since per-trade quotes may have different vendors.
  const segments = React.useMemo(
    () => getSegments({ quote, invoices: visible, quotesByTrade, trades }, selectedTrades),
    [quote, visible, quotesByTrade, trades, selectedTrades]
  );

  // Headline status: sum the three-state counts across the selected trades so the
  // banner speaks the app's one status language even when trades are combined.
  const tally = React.useMemo(() => {
    const acc = { problem: 0, review: 0, good: 0 };
    for (const seg of segments) {
      if (!seg.quote || seg.invoices.length === 0) continue;
      try {
        const rules = (vendorRules || {})[seg.quote.vendor || ''] || [];
        const c = classifyRows(aggregateInvoices(seg.quote, seg.invoices, rules, { taxExempt }).rows, tolerances).count;
        acc.problem += c.problem; acc.review += c.review; acc.good += c.good;
      } catch { /* skip a segment that can't be compared */ }
    }
    return acc;
  }, [segments, vendorRules, tolerances, taxExempt]);

  const toggleTaxExempt = () => {
    if (isSample) return;
    const next = !taxExempt;
    setTaxExempt(next); // optimistic; live listener confirms
    jobRef.update({ taxExempt: next }).catch(() => setTaxExempt(!next));
  };

  const hasData = segments.some((s) => s.quote && s.invoices.length > 0);

  if (!synced) {
    return (
      <div className="sv-loading">
        <div className="sv-spin" />
        <SummaryViewStyles />
      </div>
    );
  }

  return (
    <div className="sv">
      <div className="sv-hero">
        <div className="sv-hero-main">
          <div className="sv-hero-tag mono">JOB {job.jobNumber} · SUMMARY</div>
          <h1 className="sv-hero-h">Budget at a glance.</h1>
          <p className="sv-hero-sub">
            Where this job stands against its quoted baseline — updated automatically as invoices arrive.
          </p>
        </div>
        {/* Tax treatment — exempt jobs compare on a pre-tax (subtotal) basis.
            Lives in the header as a right-aligned control. */}
        <label className={`sv-tax ${taxExempt ? 'is-on' : ''}${isSample ? ' is-disabled' : ''}`}>
          <input type="checkbox" checked={taxExempt} disabled={isSample} onChange={toggleTaxExempt} />
          <span className="sv-tax-box"><Icon name="check" size={11} /></span>
          <span className="sv-tax-txt">
            <span className="sv-tax-row">
              <span className="sv-tax-t">Tax-exempt job</span>
              {taxExempt && <span className="sv-tax-badge">PRE-TAX</span>}
            </span>
            <span className="sv-tax-d">Compare on pre-tax subtotals — tax excluded from the budget.</span>
          </span>
        </label>
      </div>

      {/* Headline answer: anything to look at? Problems lead, then reviews. */}
      {hasData && (
        tally.problem > 0 ? (
          <button className="sv-callout sv-callout-bad" onClick={() => onNavTab && onNavTab('issues')}>
            <Icon name="flag" size={16} />
            <span className="sv-callout-txt">
              <strong className="num">{tally.problem}</strong> material{tally.problem !== 1 ? 's' : ''} need a decision
              {tally.review > 0 && <span className="sv-callout-more"> · {tally.review} more to review</span>}
            </span>
            <span className="sv-callout-go">Open issues <Icon name="chev" size={13} /></span>
          </button>
        ) : tally.review > 0 ? (
          <button className="sv-callout sv-callout-warn" onClick={() => onNavTab && onNavTab('issues')}>
            <Icon name="flag" size={16} />
            <span className="sv-callout-txt">
              <strong className="num">{tally.review}</strong> material{tally.review !== 1 ? 's' : ''} to review
            </span>
            <span className="sv-callout-go">Review issues <Icon name="chev" size={13} /></span>
          </button>
        ) : (
          <div className="sv-callout sv-callout-ok">
            <Icon name="check" size={16} />
            <span className="sv-callout-txt">Everything matches the quote — nothing to review.</span>
          </div>
        )
      )}

      {/* The budget answer itself (handles its own empty states). One panel per
          trade when the job spans several; a single panel otherwise. */}
      {multiTrade ? (
        segments.map((seg) => (
          <div key={seg.trade} className="sv-trade-block">
            <TradeSectionHeader trade={seg.trade}
              sub={seg.invoices.length ? `${seg.invoices.length} invoice${seg.invoices.length !== 1 ? 's' : ''}` : 'no invoices yet'} />
            <BudgetPanel quote={seg.quote} invoices={seg.invoices} vendorRules={vendorRules} taxExempt={taxExempt} />
          </div>
        ))
      ) : (
        <BudgetPanel quote={quote} invoices={visible} vendorRules={vendorRules} taxExempt={taxExempt} />
      )}

      {/* Where to go next */}
      <div className="sv-quick">
        <button className="sv-quick-btn" onClick={() => onNavTab && onNavTab('invoices')}>
          <Icon name="file" size={15} />
          <span><span className="sv-quick-t">Invoices</span><span className="sv-quick-d">Upload the quote & add invoices</span></span>
          <Icon name="chev" size={13} />
        </button>
        <button className="sv-quick-btn" onClick={() => onNavTab && onNavTab('materials')}>
          <Icon name="layers" size={15} />
          <span><span className="sv-quick-t">Materials</span><span className="sv-quick-d">Track quantity drawdown</span></span>
          <Icon name="chev" size={13} />
        </button>
        <button className="sv-quick-btn" onClick={() => onNavTab && onNavTab('issues')}>
          <Icon name="flag" size={15} />
          <span><span className="sv-quick-t">Issues</span><span className="sv-quick-d">Price & quantity discrepancies</span></span>
          <Icon name="chev" size={13} />
        </button>
      </div>

      <SummaryViewStyles />
    </div>
  );
}

const SummaryViewStyles = () => (
  <style>{`
    .sv { width: 100%; max-width: var(--app-max); margin: 0 auto; padding: 40px var(--app-pad) 80px; display: flex; flex-direction: column; gap: 18px; }
    .sv-trade-block { display: flex; flex-direction: column; gap: 14px; }
    .sv-loading { display: flex; align-items: center; justify-content: center; min-height: 60vh; }
    .sv-spin { width: 28px; height: 28px; border-radius: 50%; border: 2.5px solid var(--border); border-top-color: var(--accent); animation: sv-spin-kf .7s linear infinite; }
    @keyframes sv-spin-kf { to { transform: rotate(360deg); } }

    .sv-hero { display: flex; align-items: flex-end; justify-content: space-between; gap: 20px; flex-wrap: wrap; margin-bottom: 4px; }
    .sv-hero-main { min-width: 0; }
    .sv-hero-tag { font-size: 11px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--accent); margin-bottom: 5px; }
    .sv-hero-h   { font-size: 25px; font-weight: 600; letter-spacing: -0.02em; margin: 0 0 6px; }
    .sv-hero-sub { font-size: 14px; color: var(--fg-muted); margin: 0; line-height: 1.5; max-width: 560px; }

    .sv-callout {
      display: flex; align-items: center; gap: 12px; width: 100%; text-align: left;
      padding: 14px 18px; border-radius: var(--r-lg); font: inherit; font-size: 14px;
      border: 1px solid transparent;
    }
    .sv-callout-txt { flex: 1; }
    .sv-callout-warn {
      cursor: pointer; background: var(--warn-bg); color: var(--warn);
      border-color: oklch(from var(--warn) l calc(c*.5) h / .4);
      transition: background .12s, border-color .12s;
    }
    .sv-callout-warn:hover { background: oklch(from var(--warn-bg) calc(l + 0.03) c h); border-color: var(--warn); }
    .sv-callout-bad {
      cursor: pointer; background: var(--bad-bg); color: var(--bad);
      border-color: oklch(from var(--bad) l calc(c*.5) h / .4);
      transition: background .12s, border-color .12s;
    }
    .sv-callout-bad:hover { background: oklch(from var(--bad-bg) calc(l + 0.03) c h); border-color: var(--bad); }
    .sv-callout-more { font-weight: 500; opacity: 0.85; }
    .sv-callout-go { display: inline-flex; align-items: center; gap: 4px; font-size: 12.5px; font-weight: 600; }
    .sv-callout-ok {
      background: var(--ok-bg); color: var(--ok);
      border-color: oklch(from var(--ok) l calc(c*.5) h / .35);
    }

    /* Tax-exempt toggle — a compact card in the header's right slot. */
    .sv-tax { display: flex; align-items: flex-start; gap: 10px; cursor: pointer; user-select: none;
      flex-shrink: 0; max-width: 340px; padding: 11px 14px; border: 1px solid var(--border);
      border-radius: var(--r-lg); background: var(--surface); transition: border-color .12s, background .12s; }
    .sv-tax:hover { border-color: var(--border-strong); }
    .sv-tax.is-on { border-color: oklch(from var(--accent) l calc(c*.5) h / .5); background: oklch(from var(--accent) l c h / .05); }
    .sv-tax.is-disabled { opacity: .5; cursor: default; }
    .sv-tax input { position: absolute; opacity: 0; width: 0; height: 0; }
    .sv-tax-box { flex-shrink: 0; width: 18px; height: 18px; border-radius: 5px; border: 1.5px solid var(--border);
      background: var(--surface-2); display: flex; align-items: center; justify-content: center; color: transparent; transition: all .15s; margin-top: 1px; }
    .sv-tax.is-on .sv-tax-box { background: var(--accent); border-color: var(--accent); color: #fff; }
    .sv-tax-txt { display: flex; flex-direction: column; gap: 2px; }
    .sv-tax-row { display: flex; align-items: center; gap: 8px; }
    .sv-tax-t { font-size: 13px; font-weight: 600; }
    .sv-tax-d { font-size: 11.5px; color: var(--fg-muted); line-height: 1.4; }
    .sv-tax-badge { font-size: 9.5px; font-weight: 700; letter-spacing: .05em; color: var(--accent);
      background: oklch(from var(--accent) l c h / 0.14); border: 1px solid oklch(from var(--accent) l calc(c*.5) h / .4);
      padding: 2px 8px; border-radius: 999px; flex-shrink: 0; }

    .sv-quick { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px; margin-top: 6px; }
    .sv-quick-btn {
      appearance: none; font: inherit; cursor: pointer; text-align: left;
      display: flex; align-items: center; gap: 12px;
      padding: 14px 16px; border-radius: var(--r-lg);
      background: var(--surface); border: 1px solid var(--border); box-shadow: var(--shadow-1);
      color: var(--fg); transition: border-color .12s, background .12s, transform .06s;
    }
    .sv-quick-btn:hover { border-color: var(--border-strong); background: var(--surface-2); }
    .sv-quick-btn:active { transform: translateY(0.5px); }
    .sv-quick-btn > svg:first-child { color: var(--accent); flex-shrink: 0; }
    .sv-quick-btn > svg:last-child { color: var(--fg-dim); margin-left: auto; flex-shrink: 0; }
    .sv-quick-btn > span { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
    .sv-quick-t { font-size: 13.5px; font-weight: 600; }
    .sv-quick-d { font-size: 12px; color: var(--fg-dim); }
  `}</style>
);

export { BudgetPanel, SummaryView };
